import React, { useEffect, useMemo, useState } from "react";
import {
  HiOutlineTruck,
  HiOutlineEye,
  HiOutlineClipboardList,
  HiOutlineCalendar,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineRefresh,
  HiOutlineUserGroup,
  HiOutlineSearch
} from "react-icons/hi";

// Kargo Firmaları Konfigürasyonu
const CARGO_COMPANIES = {
  UPS: {
    name: "UPS",
    code: "UPS",
    trackingUrl: "https://www.ups.com/track?loc=tr_TR&tracknum=",
    apiEndpoint: "/api/tracking/ups",
    color: "bg-yellow-600"
  },
  DHL: {
    name: "DHL",
    code: "DHL", 
    trackingUrl: "https://www.dhl.com/tr-tr/home/tracking.html?tracking-id=",
    apiEndpoint: "/api/tracking/dhl",
    color: "bg-red-600"
  },
  YURTICI: {
    name: "Yurtiçi Kargo",
    code: "YURTICI",
    trackingUrl: "https://www.yurticikargo.com/tr/online-islemler/gonderi-sorgula?code=",
    apiEndpoint: "/api/tracking/yurtici",
    color: "bg-blue-600"
  },
  PTT: {
    name: "PTT Kargo",
    code: "PTT",
    trackingUrl: "https://gonderitakip.ptt.gov.tr/Track/Verify?barcode=",
    apiEndpoint: "/api/tracking/ptt",
    color: "bg-green-600"
  },
  ARAS: {
    name: "Aras Kargo",
    code: "ARAS",
    trackingUrl: "https://www.araskargo.com.tr/takip/?",
    apiEndpoint: "/api/tracking/aras",
    color: "bg-purple-600"
  }
};

// Demo veriler - Hazır gönderiler
const DEMO_READY_SALES = [
  {
    id: "SALE-2024-001",
    sale_no: "SAT-001-2024",
    partner_name: "Acıbadem Hastanesi",
    city: "İstanbul",
    country: "Türkiye",
    total_amount: "45,250.00",
    items_count: 8,
    production_completed_date: "2024-01-08",
    urgent: false,
    address: "Acıbadem Mahallesi, Çeçen Sokak No:25, Üsküdar/İstanbul"
  },
  {
    id: "SALE-2024-002", 
    sale_no: "SAT-002-2024",
    partner_name: "Berlin Medical Center",
    city: "Berlin",
    country: "Almanya",
    total_amount: "67,800.00",
    items_count: 12,
    production_completed_date: "2024-01-07",
    urgent: true,
    address: "Alexanderplatz 5, 10178 Berlin, Deutschland"
  },
  {
    id: "SALE-2024-003",
    sale_no: "SAT-003-2024", 
    partner_name: "Çukurova Üniversitesi Hastanesi",
    city: "Adana",
    country: "Türkiye",
    total_amount: "32,100.00",
    items_count: 6,
    production_completed_date: "2024-01-06",
    urgent: false,
    address: "Çukurova Üniversitesi Balcalı Hastanesi, Sarıçam/Adana"
  }
];

// Demo veriler - Sevkiyatlar
const DEMO_SHIPMENTS = [
  {
    id: "SHIP-2024-001",
    sale_id: "SALE-2024-004",
    sale_no: "SAT-004-2024",
    partner_name: "Dokuz Eylül Üniversitesi",
    city: "İzmir",
    country: "Türkiye",
    cargo_company: "YURTICI",
    tracking_number: "YK123456789TR",
    created_date: "2024-01-05",
    estimated_delivery: "2024-01-08",
    status: "in_transit",
    notes: "Fragile items - Handle with care"
  },
  {
    id: "SHIP-2024-002",
    sale_id: "SALE-2024-005", 
    sale_no: "SAT-005-2024",
    partner_name: "Paris Medical Supply",
    city: "Paris",
    country: "Fransa",
    cargo_company: "DHL",
    tracking_number: "DHL987654321FR",
    created_date: "2024-01-04",
    estimated_delivery: "2024-01-09",
    status: "delivered",
    notes: "Express delivery completed successfully"
  },
  {
    id: "SHIP-2024-003",
    sale_id: "SALE-2024-006",
    sale_no: "SAT-006-2024",
    partner_name: "Ege Üniversitesi Hastanesi", 
    city: "İzmir",
    country: "Türkiye",
    cargo_company: "UPS",
    tracking_number: "UPS456789123TR",
    created_date: "2024-01-03",
    estimated_delivery: "2024-01-07",
    status: "pending",
    notes: "Waiting for customs clearance"
  }
];

export default function LojistikPage() {
  const [sales, setSales] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [activeTab, setActiveTab] = useState("ready"); // ready | shipments
  const [loading, setLoading] = useState(false);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // API'den verileri yükle
  useEffect(() => {
    loadReadySales();
    loadShipments();
  }, []);

  const loadReadySales = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/logistics/ready-sales');
      if (response.ok) {
        const data = await response.json();
        setSales(data.length > 0 ? data : DEMO_READY_SALES);
      } else {
        console.warn('API failed, using demo data');
        setSales(DEMO_READY_SALES);
      }
    } catch (error) {
      console.error('Error loading ready sales:', error);
      setSales(DEMO_READY_SALES);
    } finally {
      setLoading(false);
    }
  };

  const loadShipments = async () => {
    try {
      setShipmentsLoading(true);
      const response = await fetch('/api/logistics/shipments');
      if (response.ok) {
        const data = await response.json();
        setShipments(data.length > 0 ? data : DEMO_SHIPMENTS);
      } else {
        console.warn('API failed, using demo data');
        setShipments(DEMO_SHIPMENTS);
      }
    } catch (error) {
      console.error('Error loading shipments:', error);
      setShipments(DEMO_SHIPMENTS);
    } finally {
      setShipmentsLoading(false);
    }
  };

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [shipmentForm, setShipmentForm] = useState({
    carrier: "",
    tracking_no: "",
    planned_delivery_date: "",
    notes: ""
  });

  // Kargo takip fonksiyonları
  const openTrackingUrl = (cargoCompany, trackingNumber) => {
    const company = CARGO_COMPANIES[cargoCompany];
    if (company && trackingNumber) {
      window.open(company.trackingUrl + trackingNumber, '_blank');
    }
  };

  const getTrackingStatus = async (cargoCompany, trackingNumber) => {
    try {
      const company = CARGO_COMPANIES[cargoCompany];
      if (!company) return null;
      
      const response = await fetch(company.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trackingNumber })
      });
      
      return await response.json();
    } catch (error) {
      console.error('Tracking error:', error);
      return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_transit': return 'bg-blue-100 text-blue-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Beklemede';
      case 'in_transit': return 'Yolda';
      case 'delivered': return 'Teslim Edildi';
      case 'cancelled': return 'İptal';
      default: return 'Bilinmiyor';
    }
  };

  useEffect(() => {
    loadReadyForLogistics();
    loadShipments();
  }, []);

  const loadReadyForLogistics = async () => {
    try {
      setLoading(true);
      // Basit yaklaşım: tüm satışları alıp frontend'de filtrele
      const res = await fetch('/api/sales');
      const data = await res.json();
      setSales(Array.isArray(data) ? data : []);
    } catch (e) {
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  const readySales = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return sales
      .filter(s => s.status === 'ready_for_delivery' || (s.items || []).some(i => ['ready', 'completed'].includes(i.production_status)))
      .filter(s => !term || (s.sale_no?.toLowerCase().includes(term) || s.partner_name?.toLowerCase().includes(term)));
  }, [sales, searchTerm]);

  const toggleItemSelection = (itemId) => {
    setSelectedItemIds(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
  };

  const openCreateShipment = (sale) => {
    setSelectedSale(sale);
    const defaultSelectable = (sale.items || []).filter(i => ['ready', 'completed'].includes(i.production_status)).map(i => i.id);
    setSelectedItemIds(defaultSelectable);
    setShipmentForm({ carrier: '', tracking_no: '', planned_delivery_date: '', notes: '' });
    setCreateModalOpen(true);
  };

  const createShipment = async () => {
    if (!selectedSale || selectedItemIds.length === 0) {
      alert('En az bir ürün seçin');
      return;
    }
    try {
      const resp = await fetch('/api/shipments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sale_id: selectedSale.id,
          item_ids: selectedItemIds,
          carrier: shipmentForm.carrier,
          tracking_no: shipmentForm.tracking_no,
          planned_delivery_date: shipmentForm.planned_delivery_date,
          notes: shipmentForm.notes
        })
      });
      if (!resp.ok) throw new Error('Sevkiyat oluşturulamadı');
      alert('Sevkiyat oluşturuldu');
      setCreateModalOpen(false);
      setSelectedSale(null);
      setSelectedItemIds([]);
      setShipmentForm({ carrier: '', tracking_no: '', planned_delivery_date: '', notes: '' });
      loadShipments();
      loadReadyForLogistics();
    } catch (e) {
      alert('Hata: ' + e.message);
    }
  };

  const markDelivered = async (shipment) => {
    try {
      const resp = await fetch(`/api/shipments/${shipment.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'delivered' })
      });
      if (!resp.ok) throw new Error('Teslim bilgisi güncellenemedi');
      alert('Teslim edildi olarak güncellendi');
      loadShipments();
      loadReadyForLogistics();
    } catch (e) {
      alert('Hata: ' + e.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-blue-900 mb-6 flex items-center gap-3 px-6 pt-6">
          <HiOutlineTruck className="w-8 h-8 text-blue-700" /> Lojistik Yönetimi
        </h1>

        <div className="bg-white rounded-2xl shadow-xl border border-blue-100">
          <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100">
            <div className="flex gap-4">
              <button
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'ready' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700'}`}
                onClick={() => setActiveTab('ready')}
              >
                Hazır Gönderiler
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'shipments' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700'}`}
                onClick={() => setActiveTab('shipments')}
              >
                Sevkiyatlar
              </button>
            </div>
            {activeTab === 'ready' && (
              <div className="relative">
                <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Satış no veya partner ara"
                />
              </div>
            )}
            <button
              onClick={() => { activeTab === 'ready' ? loadReadyForLogistics() : loadShipments(); }}
              className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-2 text-sm"
            >
              <HiOutlineRefresh className="w-4 h-4" /> Yenile
            </button>
          </div>

          {activeTab === 'ready' && (
            <div className="p-6">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Yükleniyor...</div>
              ) : readySales.length === 0 ? (
                <div className="text-center py-12 text-gray-500">Gönderilmeye hazır satış bulunamadı</div>
              ) : (
                <div className="space-y-4">
                  {readySales.map(sale => (
                    <div key={sale.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700">
                            <HiOutlineClipboardList className="w-5 h-5" />
                          </span>
                          <div>
                            <div className="font-semibold text-blue-900">{sale.sale_no}</div>
                            <div className="text-sm text-gray-600 flex items-center gap-1">
                              <HiOutlineUserGroup className="w-4 h-4 text-blue-400" />
                              {sale.partner_name}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <HiOutlineCalendar className="w-3 h-3" /> Sipariş Tarihi: {new Date(sale.order_date).toLocaleDateString('tr-TR')}
                            </div>
                          </div>
                        </div>
                        <button
                          className="px-3 py-2 rounded bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
                          onClick={() => openCreateShipment(sale)}
                        >
                          Sevkiyat Oluştur
                        </button>
                      </div>

                      <div className="mt-4 overflow-x-auto">
                        <table className="min-w-full text-sm border">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left">Seç</th>
                              <th className="px-3 py-2 text-left">Ürün</th>
                              <th className="px-3 py-2 text-left">Kod</th>
                              <th className="px-3 py-2 text-left">Miktar</th>
                              <th className="px-3 py-2 text-left">Üretim Durumu</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(sale.items || []).map(item => (
                              <tr key={item.id} className="border-t">
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    disabled={!['ready', 'completed'].includes(item.production_status) || selectedSale?.id !== sale.id}
                                    checked={selectedSale?.id === sale.id && selectedItemIds.includes(item.id)}
                                    onChange={() => toggleItemSelection(item.id)}
                                  />
                                </td>
                                <td className="px-3 py-2 font-semibold">{item.product_name}</td>
                                <td className="px-3 py-2 font-mono">{item.product_code}</td>
                                <td className="px-3 py-2">{item.quantity}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${['ready','completed'].includes(item.production_status) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                    {item.production_status === 'completed' ? 'Tamamlandı' : item.production_status === 'ready' ? 'Hazır' : 'Üretimde'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'shipments' && (
            <div className="p-6">
              {shipmentsLoading ? (
                <div className="text-center py-12 text-gray-500">Yükleniyor...</div>
              ) : shipments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">Sevkiyat bulunamadı</div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-blue-100">
                  <table className="min-w-full text-sm">
                    <thead className="bg-blue-50">
                      <tr className="text-blue-900">
                        <th className="px-3 py-2 text-left">Sevkiyat No</th>
                        <th className="px-3 py-2 text-left">Satış</th>
                        <th className="px-3 py-2 text-left">Partner</th>
                        <th className="px-3 py-2 text-left">Taşıyıcı</th>
                        <th className="px-3 py-2 text-left">Takip No</th>
                        <th className="px-3 py-2 text-left">Planlı Teslim</th>
                        <th className="px-3 py-2 text-left">Durum</th>
                        <th className="px-3 py-2 text-left">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipments.map(sh => {
                        const company = CARGO_COMPANIES[sh.cargo_company];
                        return (
                          <tr key={sh.id} className="hover:bg-blue-50 transition">
                            <td className="px-3 py-2 font-mono">{sh.id}</td>
                            <td className="px-3 py-2">{sh.sale_no || '-'}</td>
                            <td className="px-3 py-2">
                              <div>
                                <div className="font-medium">{sh.partner_name || '-'}</div>
                                <div className="text-xs text-gray-500">{sh.city}, {sh.country}</div>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                {company && (
                                  <span className={`w-3 h-3 rounded-full ${company.color}`}></span>
                                )}
                                <span>{company?.name || sh.cargo_company || '-'}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <span className="font-mono text-sm">{sh.tracking_number || '-'}</span>
                            </td>
                            <td className="px-3 py-2">{sh.estimated_delivery || '-'}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(sh.status)}`}>
                                {getStatusText(sh.status)}
                              </span>
                            </td>
                            <td className="px-3 py-2 flex gap-2">
                              <button className="px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs font-semibold flex items-center gap-1">
                                <HiOutlineEye className="w-4 h-4" /> Detay
                              </button>
                              {sh.tracking_number && company && (
                                <button
                                  onClick={() => openTrackingUrl(sh.cargo_company, sh.tracking_number)}
                                  className="px-2 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 text-xs font-semibold flex items-center gap-1"
                                >
                                  <HiOutlineSearch className="w-4 h-4" /> Takip
                                </button>
                              )}
                              {sh.status !== 'delivered' && (
                                <button
                                  className="px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-xs font-semibold flex items-center gap-1"
                                  onClick={() => markDelivered(sh)}
                                >
                                  <HiOutlineCheckCircle className="w-4 h-4" /> Teslim
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {createModalOpen && selectedSale && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl border border-blue-100">
              <div className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
                <HiOutlineTruck className="w-6 h-6 text-blue-700" /> Sevkiyat Oluştur
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                <div><b>Satış:</b> {selectedSale.sale_no}</div>
                <div><b>Partner:</b> {selectedSale.partner_name}</div>
                <div><b>Sipariş Tarihi:</b> {new Date(selectedSale.order_date).toLocaleDateString('tr-TR')}</div>
                <div><b>Toplam Tutar:</b> {selectedSale.total_amount?.toLocaleString('tr-TR')} {selectedSale.currency}</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-blue-900 mb-1">Kargo Firması</label>
                  <select
                    className="w-full px-3 py-2 border border-blue-200 rounded-lg"
                    value={shipmentForm.carrier}
                    onChange={(e) => setShipmentForm(prev => ({ ...prev, carrier: e.target.value }))}
                  >
                    <option value="">Kargo firması seçin...</option>
                    {Object.entries(CARGO_COMPANIES).map(([code, company]) => (
                      <option key={code} value={code}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-900 mb-1">Takip No</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-blue-200 rounded-lg"
                    placeholder="Takip numarası"
                    value={shipmentForm.tracking_no}
                    onChange={(e) => setShipmentForm(prev => ({ ...prev, tracking_no: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-900 mb-1">Planlı Teslim Tarihi</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-blue-200 rounded-lg"
                    value={shipmentForm.planned_delivery_date}
                    onChange={(e) => setShipmentForm(prev => ({ ...prev, planned_delivery_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-900 mb-1">Notlar</label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-blue-200 rounded-lg"
                    placeholder="Adres notu, teslimat bilgisi vb."
                    value={shipmentForm.notes}
                    onChange={(e) => setShipmentForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className="mt-4">
                <div className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <HiOutlineClipboardList className="w-4 h-4" /> Sevk Edilecek Ürünler
                </div>
                <div className="overflow-x-auto rounded border border-blue-100">
                  <table className="min-w-full text-sm">
                    <thead className="bg-blue-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Seç</th>
                        <th className="px-3 py-2 text-left">Ürün</th>
                        <th className="px-3 py-2 text-left">Kod</th>
                        <th className="px-3 py-2 text-left">Miktar</th>
                        <th className="px-3 py-2 text-left">Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedSale.items || []).map(item => (
                        <tr key={item.id} className="hover:bg-blue-50">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              disabled={!['ready', 'completed'].includes(item.production_status)}
                              checked={selectedItemIds.includes(item.id)}
                              onChange={() => toggleItemSelection(item.id)}
                            />
                          </td>
                          <td className="px-3 py-2 font-semibold">{item.product_name}</td>
                          <td className="px-3 py-2 font-mono">{item.product_code}</td>
                          <td className="px-3 py-2">{item.quantity}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${['ready','completed'].includes(item.production_status) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {item.production_status === 'completed' ? 'Tamamlandı' : item.production_status === 'ready' ? 'Hazır' : 'Üretimde'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  className="px-4 py-2 rounded bg-gray-200 text-blue-900 font-semibold hover:bg-gray-300"
                  onClick={() => { setCreateModalOpen(false); setSelectedSale(null); setSelectedItemIds([]); }}
                >
                  İptal
                </button>
                <button
                  className="px-4 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700"
                  onClick={createShipment}
                >
                  Oluştur
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

